import { Component, OnInit, computed, signal } from '@angular/core';
import { CommonModule, CurrencyPipe, DecimalPipe } from '@angular/common';
import { forkJoin } from 'rxjs';
import { FinanceiroService } from '../../../services/financeiro.service';
import { ResumoMovimentoFinanceiro } from '../../../models/financeiro.model';
import { DateUtilService } from '../../../services/dateUtil.service';

interface PeriodoMensal {
  label: string;
  dataInicio: string;
  dataFim: string;
  mesAtual: boolean;
}

interface MovimentoMensal extends PeriodoMensal, ResumoMovimentoFinanceiro {
  percentualLucro: number | null;
}

const NOMES_MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const QUANTIDADE_MESES = 4; // mês atual + 3 anteriores

@Component({
  selector: 'app-movimento-financeiro-resumo',
  standalone: true,
  imports: [CommonModule, CurrencyPipe, DecimalPipe],
  templateUrl: './movimento-financeiro-resumo.component.html',
  styleUrl: './movimento-financeiro-resumo.component.css'
})
export class MovimentoFinanceiroResumoComponent implements OnInit {
  isLoading    = signal(true);
  hasError     = signal(false);
  meses        = signal<MovimentoMensal[]>([]);
  periodoLabel = signal('');

  mesAtual = computed(() => this.meses().find(m => m.mesAtual) ?? null);

  constructor(
    private financeiroService: FinanceiroService,
    private dateUtilService: DateUtilService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    const hoje     = this.dateUtilService.dataHoje;
    const periodos = this.montarPeriodos(hoje);

    this.periodoLabel.set(`${periodos[periodos.length - 1].dataInicio} a ${hoje}`);
    this.isLoading.set(true);
    this.hasError.set(false);
    this.meses.set([]);

    forkJoin(
      periodos.map(p => this.financeiroService.getResumoMovimentoFinanceiro(p.dataInicio, p.dataFim))
    ).subscribe({
      next: resumos => {
        this.meses.set(periodos.map((p, i) => ({
          ...p,
          ...resumos[i],
          percentualLucro: this.calcularPercentualLucro(resumos[i])
        })));
        this.isLoading.set(false);
      },
      error: () => {
        this.hasError.set(true);
        this.isLoading.set(false);
      }
    });
  }

  get saldoPositivo(): boolean {
    return (this.mesAtual()?.saldo ?? 0) >= 0;
  }

  /** Margem de lucro do período: (entradas - saídas) / entradas. */
  private calcularPercentualLucro(resumo: ResumoMovimentoFinanceiro): number | null {
    if (!resumo.totalEntrada) {
      return resumo.totalSaida ? null : 0;
    }
    return (resumo.saldo / resumo.totalEntrada) * 100;
  }

  private montarPeriodos(hoje: string): PeriodoMensal[] {
    const [anoHoje, mesHoje] = hoje.split('-').map(Number);
    const periodos: PeriodoMensal[] = [];

    for (let i = 0; i < QUANTIDADE_MESES; i++) {
      let ano = anoHoje;
      let mes = mesHoje - i;
      while (mes < 1) {
        mes += 12;
        ano--;
      }
      const mm         = String(mes).padStart(2, '0');
      const ultimoDia  = new Date(ano, mes, 0).getDate();
      const dataInicio = `${ano}-${mm}-01`;
      const dataFim    = i === 0 ? hoje : `${ano}-${mm}-${String(ultimoDia).padStart(2, '0')}`;

      periodos.push({
        label: `${NOMES_MESES[mes - 1]}/${ano}`,
        dataInicio,
        dataFim,
        mesAtual: i === 0
      });
    }
    return periodos;
  }
}
